import { yupResolver } from '@hookform/resolvers/yup';
import { Invitation, UserTypeEnum } from '@wonderschool/common-base-types';
import InputMask from 'comigo-tech-react-input-mask'; // https://github.com/sanniassin/react-input-mask/issues/239
import { useFlags, useLDClient } from 'launchdarkly-react-client-sdk';
import { ChangeEvent, useEffect, useMemo, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { FaMobileAlt } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';

import { dataTestIds } from '../../../config/data-testids';
import { getSelfServeOnboardingUrl } from '../../../config/env';
import { useAuthState } from '../../../providers/authState';
import { useTranslation } from '../../../providers/i18next';
import {
  holdSignupContext,
  trackParentSignupCompleted,
} from '../../../providers/launchDarkly/signupExperiment';
import {
  RETURN_LANGUAGE_KEY,
  RETURN_URL_KEY,
  useReturnValue,
} from '../../../providers/returnValue';
import { logError, logWarning } from '../../../providers/rollbar';
import {
  INVITATION_STATUS_ROUTE,
  RETURN_ROUTE,
  SIGNIN_ROUTE,
} from '../../../routes/constants';
import { checkHasBrandingData } from '../../../utils/checkHasBrandingData';
import { ExternalLinkV2, InternalLink } from '../../Links';
import Loader from '../../LoaderV2';
import { PLACEHOLDERS } from '../consts';
import useSignup from '../Hooks/useSignup';
import LegalTextV2 from '../Parts/LegalTextV2';
import Button from '../Parts/v2/Button';
import Checkbox from '../Parts/v2/Checkbox';
import FormField from '../Parts/v2/FormField';
import FormHeader from '../Parts/v2/FormHeader';
import FormInput from '../Parts/v2/FormInput';
import { styles } from '../Parts/v2/styles';
import {
  ExtendedError,
  FORM_FIELD_NAMES,
  UserCredentialsFormSchema,
} from '../types';

// getSelfServeOnboardingUrl() is environment-aware (resolves onboardingDomain
// per env — dev/staging/prod) and already guarantees a non-empty value via its
// own fallback chain, so a context-less signup always has a non-empty, env-aware
// place to bail to without a second hard-coded URL here.
const onboardUrl = getSelfServeOnboardingUrl();

// The set of return-state user types that represent a real, provisionable
// account. Derived from the shared UserTypeEnum so this stays a single source
// of truth: a junk/leftover `type` value is rejected, but any new member added
// to UserTypeEnum is automatically accepted here — no second list to maintain.
// MissingRoll is the enum's "no real role" sentinel, so it is intentionally
// excluded (it is not a usable provisioning context).
const PROVISIONABLE_USER_TYPES: ReadonlySet<string> = new Set(
  Object.values(UserTypeEnum).filter((t) => t !== UserTypeEnum.MissingRoll)
);

interface Props {
  invitation?: Invitation;
}

const SignupForm = ({ invitation }: Props) => {
  const { txn, languageKey } = useTranslation();
  const flags = useFlags();
  const ldClient = useLDClient();
  const navigate = useNavigate();
  const { signUpNewUser, signUpInvitedUser } = useAuthState();
  const { formDefaults, brandingData, getReturnState, returnValue } =
    useReturnValue();
  const returnState = useMemo(() => getReturnState(), [getReturnState]);
  const hasInvitation = !!invitation?.id && !!invitation?.email;
  // Provider/director signups require a mobile phone. In the invited-user flow
  // invitation.type is authoritative (returnState comes from a sessionStorage
  // hash that the invite flow does not set, and can be stale from an earlier
  // director flow in the same tab), mirroring the override in onSubmit below.
  const signupType = hasInvitation ? invitation?.type : returnState?.type;
  const isDirectorSignup = signupType === 'director';
  const isParentSignup = signupType === 'parent';
  // Providers always require phone. Only parents participate in the experiment.
  // Read the flag only for parents: the SDK records exposure on flag reads.
  const parentPhoneRequired = isParentSignup
    ? flags.isSignupPhoneRequiredEnabled
    : undefined;
  const requirePhone = isDirectorSignup || parentPhoneRequired === true;
  const { userCredentialsFormSchema } = useSignup({
    requirePhone,
  });
  const methods = useForm<UserCredentialsFormSchema>({
    resolver: yupResolver(userCredentialsFormSchema),
    // Validate on submit, we're doing this because
    // network code validator requires a network request.
    mode: 'onSubmit',
  });
  const [loading, setLoading] = useState(false);
  const [unknownFormError, setUnknownFormError] = useState(false);
  // True once we've determined this signup has NO usable context (no
  // returnState / returnValue / invitation) and are bailing back to onboarding.
  // We must block the form from rendering in the meantime, otherwise a user can
  // submit the context-less form in the render tick before window.location
  // .replace() completes — creating an orphaned Firebase user with no `type`
  // and no return context, which never gets provisioned into a marketplace
  // (parent) or CCMS (provider) account. See CCMS ticket: parent tour signups
  // landing on /signup without a hash payload.
  const [redirectingNoContext, setRedirectingNoContext] = useState(false);

  const {
    formState: { isSubmitting, errors },
    handleSubmit,
    register,
    setValue,
    setError,
    reset,
  } = methods;

  const hasBranding = useMemo(() => {
    return checkHasBrandingData(brandingData);
  }, [brandingData]);

  // ensure user context exists AND is usable for provisioning
  useEffect(() => {
    // A signup is only provisionable if we can tell the backend WHAT kind of
    // account to build and WHERE to return. That signal is:
    //   - an invitation (invite flow carries its own type), OR
    //   - a resolved user type on the return state (returnState.type, e.g.
    //     'parent' | 'director'), OR
    //   - a return URL on the return value (RETURN_URL_KEY / 'ret').
    // returnState here is the decoded `rts` object and returnValue is the full
    // decoded payload (which is where `ret` lives).
    //
    // The previous guard only bailed when returnState was COMPLETELY empty.
    // That missed the partial/stale-context case: a returnState that has some
    // keys (e.g. left over from an earlier director flow in the same tab) but
    // no usable `type`/`ret`. In that case the non-invite submit path never
    // sets `type`, so signUpNewUser mints a typeless, orphaned Firebase shell
    // that never provisions into a marketplace (parent) or CCMS (provider)
    // account -- the exact symptom behind the parent-tour "something went
    // wrong" reports. Treat "context present but not usable" as no-context too.
    //
    // `type` is validated against the shared UserTypeEnum (via
    // PROVISIONABLE_USER_TYPES) rather than "any truthy value", so a leftover /
    // junk type is rejected while any future enum member is accepted with no
    // change here.
    const resolvedType = (returnState as { type?: string } | null | undefined)
      ?.type;
    const hasUsableType =
      !!resolvedType && PROVISIONABLE_USER_TYPES.has(resolvedType);
    const hasReturnUrl = !!(
      returnValue as Record<string, unknown> | null | undefined
    )?.[RETURN_URL_KEY];

    const hasUsableContext = hasInvitation || hasUsableType || hasReturnUrl;

    if (!hasUsableContext) {
      // Reached /signup without a usable provisioning context (bare/bookmarked
      // link, a dropped URL-hash fragment, or a stale tab). Do NOT let the
      // form render/submit -- that produces an unprovisioned, orphaned account.
      // Bail back to the self-serve onboarding funnel, which re-establishes the
      // user type (parent vs provider) and the return URL.
      setRedirectingNoContext(true);
      window.location.replace(onboardUrl);
    }
  }, [returnState, returnValue, hasInvitation, invitation]);

  // check for and instantiate newSupply flag + email
  useEffect(() => {
    if (!returnState || !Object.keys(returnState || {}).length) {
      return;
    }

    setValue(FORM_FIELD_NAMES.NEW_SUPPLY, returnState?.newSupply ?? false);
    setValue(FORM_FIELD_NAMES.EMAIL, returnState?.email ?? '');
  }, [returnState, setValue, returnValue]);

  // hydrate form with invite data
  useEffect(() => {
    if (!invitation) {
      return;
    } else if (!invitation?.id || !invitation?.email) {
      logWarning('Unexpected: invitation without id/email', { invitation });
      return;
    }

    // Populate form with invitation data if available
    const {
      firstName = '',
      lastName = '',
      email = '',
      phone = '',
      id = '',
    } = invitation;

    // must use reset here, rather than setValue
    // because the phone mask will not work as intended otherwise
    reset({
      [FORM_FIELD_NAMES.FIRST_NAME]: firstName,
      [FORM_FIELD_NAMES.LAST_NAME]: lastName,
      [FORM_FIELD_NAMES.PHONE]: phone,
      [FORM_FIELD_NAMES.EMAIL]: email,
      [FORM_FIELD_NAMES.INVITATION_ID]: id,
    });
    // TODO: REMOVE THE NEXT TWO LINES
    setValue(FORM_FIELD_NAMES.NETWORK_CODE, 'test value');
    setValue(FORM_FIELD_NAMES.NEW_SUPPLY, false);
    // TODO: REMOVE setValue as a dependency
  }, [invitation, reset, setValue]);

  const onSubmit = handleSubmit(async (data: UserCredentialsFormSchema) => {
    // Only track visitors who received a real flag evaluation, not the fallback
    // rendered while LaunchDarkly is unavailable or still initializing.
    const experimentClient =
      typeof parentPhoneRequired === 'boolean' ? ldClient : undefined;
    const releaseContext = holdSignupContext(experimentClient);
    const signupData: UserCredentialsFormSchema & {
      type?: string;
    } = {
      ...data,
      // always ensure lowercase
      email: data.email.toLocaleLowerCase(),
    };

    try {
      setLoading(true);

      const optData = {
        [RETURN_LANGUAGE_KEY]: languageKey,
      };

      let signupResult: ExtendedError | null = null;

      // Handles signup with invitation.
      if (hasInvitation || invitation) {
        // Send invitation id for post signup user profile update.
        signupData.invitationId = invitation?.id;
        // invites should not be considered newSupply
        signupData.newSupply = false;
        // we MUST ensure that return context does not override the invite type.
        if (invitation.type) {
          signupData.type = invitation.type;
        }
        signupResult = await signUpInvitedUser(signupData, optData);
      } else {
        signupResult = await signUpNewUser(signupData, optData, hasBranding);
      }

      // primarily concerned with catching in-use email errors
      if (!signupResult || signupResult?.errorCode) {
        if (signupResult?.errorCode === 'email') {
          // The email already has an account. An invited user is a returning
          // user, not a new one — route them to sign in (carrying their email)
          // instead of dead-ending on the create-account form.
          if (hasInvitation || invitation) {
            navigate(SIGNIN_ROUTE, {
              replace: true,
              state: {
                email: signupData.email,
                invitationId: invitation?.id,
                invitationType: invitation?.type,
              },
            });
            return null;
          }
          setError(FORM_FIELD_NAMES.EMAIL, {
            message: txn('This email address is already in use'),
          });
          setLoading(false);
          return null;
        }
        // unknown issue (caught below)
        throw new Error('Signup failed');
      }

      await trackParentSignupCompleted(experimentClient, !!signupData.phone);

      if (hasInvitation) {
        navigate(INVITATION_STATUS_ROUTE, {
          replace: true,
          state: {
            ...data,
            invitationType: invitation?.type,
            invitationId: invitation?.id,
          },
        });
      } else {
        // Normal return
        navigate(RETURN_ROUTE, { replace: true });
      }
    } catch (err: any) {
      logError(`SignUpForm: ${err.message}`, err);
      setUnknownFormError(true);
      setLoading(false);
    } finally {
      releaseContext();
    }
  });

  // Block the context-less form from rendering while we bail back to
  // onboarding. Without this, the bare "Create your account" form is briefly
  // interactive and a fast user can submit it, creating an orphaned Firebase
  // account with no provisioning context.
  if (redirectingNoContext) {
    return (
      <Loader
        hasBranding={hasBranding}
        text={txn('One moment while we get everything ready')}
      />
    );
  }

  // local loading screen which matches that of the invitation-status page.
  if (loading) {
    return (
      <Loader
        hasBranding={hasBranding}
        text={txn('One moment while we get everything ready')}
      />
    );
  }

  // catch all for broken states
  if (unknownFormError) {
    return (
      <FormHeader
        text={txn('Oops, something went wrong')}
        subtext={txn('We could not create your account')}
      />
    );
  }

  return (
    <div className="space-y-10">
      {/* IMPORTANT: if there is alternate branding, no mention of wonderschool is permitted */}
      <div>
        <FormHeader
          text={
            !hasBranding && hasInvitation
              ? txn('{{firstName}}, you have an invitation!', {
                  firstName: invitation?.firstName,
                })
              : txn('Create your account')
          }
          subtext={
            !hasBranding && hasInvitation ? (
              <div>
                {txn('Welcome to {{organizationName}}.', {
                  organizationName: invitation.organizationName,
                })}
                <div>
                  {txn('Already have an account?')}
                  <InternalLink to={SIGNIN_ROUTE}>
                    <> {txn('Log in')}</>
                  </InternalLink>
                </div>
              </div>
            ) : (
              <div>
                {txn('Already have an account?')}
                <InternalLink to={SIGNIN_ROUTE}>
                  <> {txn('Log in')}</>
                </InternalLink>
              </div>
            )
          }
        />
      </div>
      <div className="space-y-4">
        <FormProvider {...methods}>
          <form onSubmit={onSubmit} noValidate className="space-y-6">
            {/* hidden inputs carried forward... */}
            {/* note: email input is generally hidden, but handled below */}
            <FormInput
              hidden
              type="checkbox"
              {...register(FORM_FIELD_NAMES.NEW_SUPPLY)}
              data-testid={dataTestIds.signupForm.newSupplyCheckbox}
            />
            <FormInput
              hidden
              type="input"
              {...register(FORM_FIELD_NAMES.INVITATION_ID)}
              data-testid={dataTestIds.signupForm.invitationId}
            />

            <div className="flex gap-x-4 w-full">
              <div className="w-full">
                <FormField
                  // hide for invite
                  {...(invitation?.id ? { hidden: true } : {})}
                  label={PLACEHOLDERS[FORM_FIELD_NAMES.FIRST_NAME]}
                  required
                  placeholder={PLACEHOLDERS[FORM_FIELD_NAMES.FIRST_NAME]}
                  disabled={isSubmitting}
                  defaultValue={formDefaults?.firstName}
                  error={errors.firstName}
                  {...register(FORM_FIELD_NAMES.FIRST_NAME)}
                  data-testid={dataTestIds.signupForm.firstName}
                />
              </div>
              <div className="w-full">
                <FormField
                  // hide for invite
                  {...(invitation?.id ? { hidden: true } : {})}
                  label={PLACEHOLDERS[FORM_FIELD_NAMES.LAST_NAME]}
                  required
                  placeholder={PLACEHOLDERS[FORM_FIELD_NAMES.LAST_NAME]}
                  disabled={isSubmitting}
                  defaultValue={formDefaults?.lastName}
                  error={errors.lastName}
                  {...register(FORM_FIELD_NAMES.LAST_NAME)}
                  data-testid={dataTestIds.signupForm.lastName}
                />
              </div>
            </div>

            <FormField
              label={PLACEHOLDERS[FORM_FIELD_NAMES.PHONE]}
              required={requirePhone}
              placeholder={PLACEHOLDERS[FORM_FIELD_NAMES.PHONE]}
              type="tel"
              disabled={isSubmitting}
              defaultValue={formDefaults?.phone}
              error={errors.phone}
              {...register(FORM_FIELD_NAMES.PHONE)}
              // note: these attributes must come after spreading register
              onBlur={(e) => {
                // validate password confirm on blur
                const value = e?.currentTarget?.value || e?.target?.value;
                setValue(FORM_FIELD_NAMES.PHONE, value, {
                  shouldValidate: true,
                });
              }}
              // clears active error state on input delete
              onChange={(e: ChangeEvent<HTMLInputElement>) => {
                const value = e.currentTarget.value || e.target.value;
                if (errors[FORM_FIELD_NAMES.PHONE]) {
                  setValue(FORM_FIELD_NAMES.PHONE, value, {
                    shouldValidate: true,
                  });
                } else {
                  setValue(FORM_FIELD_NAMES.PHONE, value);
                }
              }}
              as={InputMask}
              mask={'(999) 999-9999'}
              data-testid={dataTestIds.signupForm.mobile}
            />
            {isDirectorSignup && (
              <div className="bg-purple-50 border-l-4 border-blue-800 p-4 rounded-r-lg">
                <div className="flex gap-3 items-start">
                  <div className="pt-1">
                    <FaMobileAlt className="h-4 w-4 text-blue-800" />
                  </div>
                  <div>
                    <div className="text-sm font-semibold text-foreground">
                      {txn('Why do we need your phone number?')}
                    </div>
                    {/*
                      The texting disclosure for the number above. Carriers
                      reviewing an A2P 10DLC campaign check for the brand,
                      what is sent, frequency, rates, HELP, STOP and links to
                      the terms and privacy policy, so every one is here.
                      A branded (white-labeled) render may not mention
                      Wonderschool, so it keeps everything but the name and
                      our links; the partner's terms are on the checkbox below.
                      The texting line's welcome uses this number only when the
                      separate "text me" box below is ticked.
                    */}
                    <div
                      className="text-sm text-muted-foreground"
                      data-testid={dataTestIds.signupForm.textingDisclosure}
                    >
                      {hasBranding ? (
                        txn(
                          'We will text this number when families want to tour, message, or apply to your school. Message frequency varies. Msg & data rates may apply. Reply HELP for help or STOP to opt out.'
                        )
                      ) : (
                        <>
                          {txn(
                            'Wonderschool will text this number when families want to tour, message, or apply to your school. Message frequency varies. Msg & data rates may apply. Reply HELP for help or STOP to opt out. See our'
                          )}{' '}
                          <ExternalLinkV2 href="https://www.wonderschool.com/corp/terms">
                            {txn('Terms of Use')}
                          </ExternalLinkV2>{' '}
                          {txn('and')}{' '}
                          <ExternalLinkV2 href="https://www.wonderschool.com/corp/privacy">
                            {txn('Privacy Policy')}
                          </ExternalLinkV2>
                          .
                        </>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            )}
            {/* Optional and unticked: the phone is required, so consent can't ride on it. Hidden when branded because the line speaks as Wonderschool. */}
            {isDirectorSignup && !hasBranding && (
              <Checkbox
                {...register(FORM_FIELD_NAMES.SMS_CONSENT)}
                data-testid={dataTestIds.signupForm.smsConsent}
              >
                {txn(
                  'Also text me from Wonderschool about my listing and setup. Message frequency varies. Msg & data rates may apply. Reply HELP for help or STOP to opt out. Not required to sign up.'
                )}
              </Checkbox>
            )}
            {/*
              hide this input for standard signups,
              networks signups via join pages wont have this info, so the user must be able to enter it
            */}
            {invitation?.email || returnState?.email ? (
              <FormInput
                hidden
                type="email"
                {...register(FORM_FIELD_NAMES.EMAIL)}
                data-testid={dataTestIds.signupForm.email}
              />
            ) : (
              <FormField
                label={PLACEHOLDERS[FORM_FIELD_NAMES.EMAIL]}
                required
                placeholder={PLACEHOLDERS[FORM_FIELD_NAMES.EMAIL]}
                type="email"
                disabled={isSubmitting}
                defaultValue={formDefaults?.email}
                error={errors.email}
                {...register(FORM_FIELD_NAMES.EMAIL)}
                data-testid={dataTestIds.signupForm.email}
              />
            )}

            <div className="space-y-2">
              <FormField
                label={PLACEHOLDERS[FORM_FIELD_NAMES.PASSWORD]}
                required
                placeholder={PLACEHOLDERS[FORM_FIELD_NAMES.PASSWORD]}
                type="password"
                autoComplete="new-password"
                disabled={isSubmitting}
                error={errors.password}
                {...register(FORM_FIELD_NAMES.PASSWORD)}
                // note: this attribute must come after spreading register
                onBlur={(e) => {
                  // validate password confirm on blur
                  const value = e?.currentTarget?.value || e?.target?.value;
                  setValue(FORM_FIELD_NAMES.PASSWORD, value, {
                    shouldValidate: true,
                  });
                }}
                onChange={(e) => {
                  const value = e?.currentTarget?.value || e?.target?.value;
                  if (errors[FORM_FIELD_NAMES.PASSWORD]) {
                    setValue(FORM_FIELD_NAMES.PASSWORD, value, {
                      shouldValidate: true,
                    });
                  } else {
                    setValue(FORM_FIELD_NAMES.PASSWORD, value);
                  }
                }}
                data-testid={dataTestIds.signupForm.password}
              />
              <div className="text-xs">
                {txn(
                  'Password must be 6 or more characters. Password should contain an uppercase letter, lowercase letter, number, and symbol.'
                )}
              </div>
            </div>

            <FormField
              label={PLACEHOLDERS[FORM_FIELD_NAMES.PASSWORD_CONFIRM]}
              required
              placeholder={PLACEHOLDERS[FORM_FIELD_NAMES.PASSWORD_CONFIRM]}
              type="password"
              autoComplete="new-password"
              disabled={isSubmitting}
              error={errors.passwordConfirm}
              {...register(FORM_FIELD_NAMES.PASSWORD_CONFIRM)}
              // note: this attribute must come after spreading register
              onBlur={(e) => {
                // validate password confirm on blur
                const value = e?.currentTarget?.value || e?.target?.value;
                setValue(FORM_FIELD_NAMES.PASSWORD_CONFIRM, value, {
                  shouldValidate: true,
                });
              }}
              onChange={(e) => {
                const value = e?.currentTarget?.value || e?.target?.value;
                if (errors[FORM_FIELD_NAMES.PASSWORD_CONFIRM]) {
                  setValue(FORM_FIELD_NAMES.PASSWORD_CONFIRM, value, {
                    shouldValidate: true,
                  });
                } else {
                  setValue(FORM_FIELD_NAMES.PASSWORD_CONFIRM, value);
                }
              }}
              data-testid={dataTestIds.signupForm.passwordConfirm}
            />

            <Checkbox
              required
              error={errors[FORM_FIELD_NAMES.TERMS_AND_CONDITIONS]}
              {...register(FORM_FIELD_NAMES.TERMS_AND_CONDITIONS)}
              data-testid={dataTestIds.signupForm.termsAndConditions}
            >
              <LegalTextV2 />
            </Checkbox>

            <div className="pt-16 space-y-4">
              <Button type="submit" text={txn('Next')} />
              <button
                type="button"
                className={styles.btnLink}
                onClick={() => history.back()}
              >
                {txn('Go back')}
              </button>
            </div>
          </form>
        </FormProvider>
      </div>
    </div>
  );
};

export default SignupForm;
